/**
 * Output Token Service
 *
 * Extracts and converts output parameters from operation manifests and swagger
 * specs into tokens for use in the token picker.
 *
 * Based on LogicAppsUX patterns:
 * - getOutputParametersFromManifest
 * - getOutputParametersFromSwagger
 * - convertOutputsToTokens
 */

import type { OperationManifest, BuiltInOutput } from '@/types/operationManifest';
import type { Token, TokenType, SchemaObject } from '@/types/valueSegment';
import { TokenBrandColors } from '@/types/valueSegment';
import type { SwaggerSpec } from '@/types/swagger';
import { SwaggerParser } from '@/parsers/SwaggerParser';
import type { ParameterInfo } from '@/types/operationParameter';
import { optionalBracketAccessor, pathToOptionalAccessors } from '@/utils/expressions';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Output information extracted from manifest or swagger
 */
export interface OutputInfo {
  key: string;
  name: string;
  /** Full property path for nested properties (e.g., "memberSettings.allowDeleteChannels") */
  path?: string;
  title: string;
  type: string;
  description?: string;
  format?: string;
  required?: boolean;
  schema?: SchemaObject;
  source?: 'body' | 'headers' | 'statusCode' | 'outputs';
  isAdvanced?: boolean;
  isDynamic?: boolean;
  arrayDetails?: {
    itemSchema?: SchemaObject;
    parentArray?: string;
  };
}

/**
 * Operation metadata for token generation
 */
export interface OperationMetadata {
  actionName: string;
  operationType: string;
  iconUri?: string;
  brandColor?: string;
  isTrigger?: boolean;
}

// =============================================================================
// MANIFEST OUTPUT EXTRACTION
// =============================================================================

/**
 * Extract output parameters from an operation manifest.
 * Handles both schema-based outputs and built-in outputs.
 */
export function getOutputsFromManifest(
  manifest: OperationManifest
): OutputInfo[] {
  const outputs: OutputInfo[] = [];
  const props = manifest.properties;

  // Extract built-in outputs first (they take priority)
  if (props.outputTokens?.builtIns) {
    outputs.push(...extractBuiltInOutputs(props.outputTokens.builtIns));
  }

  // Extract outputs from the outputs schema
  // Look for body properties first (standard manifest structure for managed connectors)
  if (props.outputs?.properties?.body) {
    const bodySchema = props.outputs.properties.body as SchemaObject;
    outputs.push(...extractSchemaOutputsRecursive(bodySchema, '', 'body'));
  } else if (props.outputs) {
    // For actions without a body (like Compose), extract from outputs with 'outputs' source
    // Skip extracting 'body' property since it's not a true body response
    outputs.push(...extractSchemaOutputsRecursive(props.outputs, '', 'outputs', 10, 0, true));
  }

  return outputs;
}

/**
 * Extract outputs from built-in output definitions
 */
function extractBuiltInOutputs(builtIns: BuiltInOutput[]): OutputInfo[] {
  return builtIns.map((builtIn) => ({
    key: `builtin_${builtIn.name}`,
    name: builtIn.name,
    title: builtIn.title,
    type: builtIn.type,
    required: builtIn.required,
    source: inferSourceFromName(builtIn.name),
  }));
}

/**
 * Extract outputs from a schema object recursively.
 * This extracts all properties at all levels with proper path tracking.
 *
 * @param schema - The schema to extract outputs from
 * @param basePath - The dot-notation path to this schema (e.g., "memberSettings")
 * @param source - The output source type
 * @param maxDepth - Maximum depth for nested extraction (default: 10)
 * @param currentDepth - Current depth level (internal use)
 */
function extractSchemaOutputsRecursive(
  schema: SchemaObject,
  basePath: string,
  source: 'body' | 'headers' | 'statusCode' | 'outputs',
  maxDepth: number = 10,
  currentDepth: number = 0,
  skipBodyProperty: boolean = false
): OutputInfo[] {
  const outputs: OutputInfo[] = [];

  // Stop recursion if we've gone too deep
  if (currentDepth > maxDepth) return outputs;

  if (schema.type === 'object' && schema.properties) {
    for (const [propName, propSchema] of Object.entries(schema.properties)) {
      // Skip 'body' property when extracting from outputs directly (not a true body response)
      if (skipBodyProperty && propName === 'body') continue;
      const path = basePath ? `${basePath}.${propName}` : propName;
      const isRequired = isPropertyRequired(schema, propName);
      const isAdvanced = propSchema['x-ms-visibility'] === 'advanced';
      const isInternal = propSchema['x-ms-visibility'] === 'internal';

      // Skip internal properties
      if (isInternal) continue;

      const output: OutputInfo = {
        key: `output_${path.replace(/\./g, '_')}`,
        name: propName,
        path: path, // Full property path for nested access
        title: propSchema.title ?? formatLabel(propName),
        type: propSchema.type ?? 'string',
        required: isRequired,
        schema: propSchema,
        source,
        isAdvanced,
      };
      if (propSchema.description) {
        output.description = propSchema.description;
      }
      if (propSchema.format) {
        output.format = propSchema.format;
      }
      outputs.push(output);

      // Recursively extract nested object properties
      if (propSchema.type === 'object' && propSchema.properties) {
        outputs.push(
          ...extractSchemaOutputsRecursive(
            propSchema,
            path,
            source,
            maxDepth,
            currentDepth + 1
          )
        );
      }

      // Handle array items - extract properties from item schema
      if (propSchema.type === 'array' && propSchema.items) {
        const itemSchema = propSchema.items as SchemaObject;

        // If array items are objects with properties, extract those
        if (itemSchema.type === 'object' && itemSchema.properties) {
          outputs.push(
            ...extractSchemaOutputsRecursive(
              itemSchema,
              path,
              source,
              maxDepth,
              currentDepth + 1
            )
          );
        }
      }
    }
  }

  return outputs;
}

// =============================================================================
// SWAGGER OUTPUT EXTRACTION
// =============================================================================

/**
 * Extract output parameters from a swagger spec for a given operation
 */
export function getOutputsFromSwagger(
  swagger: SwaggerSpec,
  operationId: string
): OutputInfo[] {
  const parser = new SwaggerParser(swagger);
  const parameters = parser.getOutputParameters(operationId, {
    excludeInternalOutputs: true,
    excludeAdvancedOutputs: false,
  });

  return parameters.map((param) => parameterInfoToOutputInfo(param));
}

/**
 * Convert a ParameterInfo to OutputInfo
 */
function parameterInfoToOutputInfo(param: ParameterInfo): OutputInfo {
  const output: OutputInfo = {
    key: `output_${param.parameterKey}`,
    name: param.parameterName,
    title: param.label,
    type: param.schema?.type ?? 'string',
    required: param.required,
    source: 'body',
    isAdvanced: param.visibility === 'advanced',
  };
  if (param.schema?.description) {
    output.description = param.schema.description;
  }
  if (param.schema?.format) {
    output.format = param.schema.format;
  }
  if (param.schema) {
    output.schema = param.schema;
  }
  return output;
}

// =============================================================================
// TOKEN CONVERSION
// =============================================================================

/**
 * Convert output parameters to tokens for the token picker.
 * This is the main entry point for converting outputs to UI tokens.
 */
export function convertOutputsToTokens(
  outputs: OutputInfo[],
  metadata: OperationMetadata
): Token[] {
  const { actionName, brandColor, isTrigger } = metadata;
  const tokenBrandColor = brandColor ?? TokenBrandColors.outputs;

  return outputs.map((output) => {
    const token: Token = {
      key: `${actionName}_${output.key}`,
      title: output.title,
      name: output.name,
      tokenType: 'outputs' as TokenType,
      source: output.source ?? 'body',
      type: output.type,
      brandColor: tokenBrandColor,
      value: generateOutputExpression(actionName, output, isTrigger),
      description: output.description ?? `${output.title} from ${actionName}`,
    };

    // Only set actionName for non-triggers
    if (!isTrigger) {
      token.actionName = actionName;
    }

    if (output.required !== undefined) {
      token.required = output.required;
    }
    if (output.schema) {
      token.schema = output.schema;
    }
    if (output.format) {
      token.format = output.format;
    }

    if (output.arrayDetails?.itemSchema) {
      token.arrayDetails = {
        itemSchema: output.arrayDetails.itemSchema,
      };
    }

    return token;
  });
}

/**
 * Generate the expression for accessing an output.
 * Uses Otto's JavaScript `${...}` syntax with optional chaining for nested
 * properties (`?.['key']`). The accessor format is owned by
 * {@link optionalBracketAccessor} in `utils/expressions`.
 *
 * Examples:
 * - Simple: `${body('Get_a_team')?.['displayName']}`
 * - Nested: `${body('Get_a_team')?.['memberSettings']?.['allowDeleteChannels']}`
 */
function generateOutputExpression(
  actionName: string,
  output: OutputInfo,
  isTrigger?: boolean
): string {
  const source = output.source ?? 'body';
  const propertyPath = output.path ?? output.name;
  const accessors = pathToOptionalAccessors(propertyPath);

  const bodyRoot = isTrigger ? 'triggerBody()' : `body('${actionName}')`;
  const outputsRoot = isTrigger ? 'triggerOutputs()' : `outputs('${actionName}')`;

  switch (source) {
    case 'body':
      return propertyPath === 'body'
        ? `\${${bodyRoot}}`
        : `\${${bodyRoot}${accessors}}`;
    case 'headers':
      return `\${${outputsRoot}${optionalBracketAccessor('headers')}}`;
    case 'statusCode':
      return `\${${outputsRoot}${optionalBracketAccessor('statusCode')}}`;
    default:
      return `\${${outputsRoot}${accessors}}`;
  }
}

/**
 * Generate default tokens for an action when no manifest/swagger is available.
 * Provides common outputs like body, headers, statusCode, outputs.
 */
export function generateDefaultTokens(
  metadata: OperationMetadata
): Token[] {
  const { actionName, operationType, brandColor, isTrigger } = metadata;
  const tokenBrandColor = brandColor ?? TokenBrandColors.outputs;
  const tokens: Token[] = [];
  const opTypeLower = operationType.toLowerCase();

  // Check for operations that don't have a body response (like Compose, Variable actions)
  const isComposeOrVariable =
    opTypeLower.includes('compose') ||
    opTypeLower.includes('variable') ||
    opTypeLower.includes('initializevariable') ||
    opTypeLower.includes('setvariable') ||
    opTypeLower.includes('appendtoarrayvariable') ||
    opTypeLower.includes('appendtostringvariable') ||
    opTypeLower.includes('incrementvariable') ||
    opTypeLower.includes('decrementvariable');

  // Body output (for most operations, but not Compose/Variable types)
  if (!isComposeOrVariable) {
    const bodyToken: Token = {
      key: `${actionName}_body`,
      title: 'Body',
      name: 'body',
      tokenType: 'outputs' as TokenType,
      source: 'body',
      type: 'object',
      brandColor: tokenBrandColor,
      value: isTrigger
        ? `\${triggerBody()}`
        : `\${body('${actionName}')}`,
      description: `The body of the ${isTrigger ? 'trigger' : 'action'} response`,
    };
    if (!isTrigger) {
      bodyToken.actionName = actionName;
    }
    tokens.push(bodyToken);
  }

  // HTTP-like operations get additional outputs
  const isHttpLike =
    opTypeLower.includes('http') ||
    opTypeLower.includes('apiconnection') ||
    opTypeLower.includes('request');

  if (isHttpLike) {
    // Status code
    const statusToken: Token = {
      key: `${actionName}_statusCode`,
      title: 'Status Code',
      name: 'statusCode',
      tokenType: 'outputs' as TokenType,
      source: 'statusCode',
      type: 'integer',
      brandColor: tokenBrandColor,
      value: isTrigger
        ? `\${triggerOutputs()['statusCode']}`
        : `\${outputs('${actionName}')['statusCode']}`,
      description: `HTTP status code from ${actionName}`,
    };
    if (!isTrigger) {
      statusToken.actionName = actionName;
    }
    tokens.push(statusToken);

    // Headers
    const headersToken: Token = {
      key: `${actionName}_headers`,
      title: 'Headers',
      name: 'headers',
      tokenType: 'outputs' as TokenType,
      source: 'headers',
      type: 'object',
      brandColor: tokenBrandColor,
      value: isTrigger
        ? `\${triggerOutputs()['headers']}`
        : `\${outputs('${actionName}')['headers']}`,
      description: `Response headers from ${actionName}`,
    };
    if (!isTrigger) {
      headersToken.actionName = actionName;
    }
    tokens.push(headersToken);
  }

  // Generic outputs accessor
  const outputsToken: Token = {
    key: `${actionName}_outputs`,
    title: 'Outputs',
    name: 'outputs',
    tokenType: 'outputs' as TokenType,
    source: 'outputs',
    type: 'object',
    brandColor: tokenBrandColor,
    value: isTrigger
      ? `\${triggerOutputs()}`
      : `\${outputs('${actionName}')}`,
    description: `All outputs from ${actionName}`,
  };
  if (!isTrigger) {
    outputsToken.actionName = actionName;
  }
  tokens.push(outputsToken);

  return tokens;
}

/**
 * Get tokens for an operation, combining manifest/swagger outputs with defaults
 */
export function getTokensForOperation(
  manifest: OperationManifest | null,
  swagger: SwaggerSpec | null,
  operationId: string | null,
  metadata: OperationMetadata
): Token[] {
  const tokens: Token[] = [];

  // Check if tokens are disabled in manifest
  if (manifest?.properties.outputTokens?.disableTokens) {
    return [];
  }

  // Try to extract from manifest first
  if (manifest) {
    const outputs = getOutputsFromManifest(manifest);
    if (outputs.length > 0) {
      tokens.push(...convertOutputsToTokens(outputs, metadata));
    }
  }

  // If no manifest outputs, try swagger
  if (tokens.length === 0 && swagger && operationId) {
    const outputs = getOutputsFromSwagger(swagger, operationId);
    if (outputs.length > 0) {
      tokens.push(...convertOutputsToTokens(outputs, metadata));
    }
  }

  // If still no tokens, generate defaults
  if (tokens.length === 0) {
    tokens.push(...generateDefaultTokens(metadata));
  }

  return tokens;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Check if a property is required in a schema
 */
function isPropertyRequired(
  schema: SchemaObject,
  propertyName: string
): boolean {
  if (!schema.required) return false;
  if (Array.isArray(schema.required)) {
    return schema.required.includes(propertyName);
  }
  return false;
}

/**
 * Infer output source from output name
 */
function inferSourceFromName(
  name: string
): 'body' | 'headers' | 'statusCode' | 'outputs' {
  const lower = name.toLowerCase();
  if (lower === 'body' || lower === 'content') return 'body';
  if (lower === 'headers' || lower.includes('header')) return 'headers';
  if (lower === 'statuscode' || lower === 'status') return 'statusCode';
  return 'outputs';
}

/**
 * Format a name as a display label
 */
function formatLabel(name: string): string {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}
